import React from "react";
import { FormattedMessage, useIntl } from "react-intl";
import {
	Card,
	CircularProgress,
	Grid,
	IconButton,
	styled,
	Table,
	TableBody,
	TableCell,
	TableContainer,
	TableHead,
	TableRow,
	Typography,
} from "@mui/material";
import Refresh from "@mui/icons-material/Refresh";

import type { Corrugate } from "@/schemas/material/corrugate";
import type { MachineGroup } from "@/schemas/machine-group";
import type { ProductionGroup } from "@/schemas/production-group";

import { ConfigurationPageLayout } from "../../components/configuration-page-layout";
import { ProductionGroupDeleteButton } from "../components/production-group-delete-button";
import { ProductionGroupEditButton } from "../components/production-group-edit-button";
import { ProductionGroupHistoryButton } from "../components/production-group-history-button";
import { ProductionGroupNewButton } from "../components/production-group-new-button";

const StyledTableRow = styled(TableRow)(({ theme }) => ({
	"&:nth-of-type(even)": {
		backgroundColor: theme.palette.action.hover,
	},
}));

export type ConfiguredMachineGroups = Record<string, string>;
export type ConfiguredCorrugates = Record<string, string[]>;

const deletedZfoldLabel = "Deleted z-Fold";

export interface ProductionGroupsTemplateProps {
	readonly productionGroups: ProductionGroup[];
	readonly configuredMachineGroups: ConfiguredMachineGroups;
	readonly configuredCorrugates: ConfiguredCorrugates;
	readonly machineGroups: MachineGroup[];
	readonly corrugates: Corrugate[];
	readonly isLoading: boolean;
	readonly isError: boolean;
	readonly onRetry: () => void;
}

export const ProductionGroupsTemplate: React.FC<ProductionGroupsTemplateProps> = ({
	productionGroups,
	configuredMachineGroups,
	configuredCorrugates,
	machineGroups,
	corrugates,
	isLoading,
	isError,
	onRetry,
}) => {
	const intl = useIntl();

	return (
		<ConfigurationPageLayout
			title={intl.formatMessage({ id: "Production Groups", defaultMessage: "Production Groups" })}
			actions={<ProductionGroupNewButton />}
		>
			<Card component={Grid} container direction="column">
				<TableContainer sx={{ flex: 1 }}>
					<Table stickyHeader>
						<TableHead>
							<TableRow>
								<TableCell>
									<FormattedMessage id="Name" defaultMessage="Name" />
								</TableCell>
								<TableCell>
									<FormattedMessage id="Description" defaultMessage="Description" />
								</TableCell>
								<TableCell>
									<FormattedMessage id="Configured z-Folds" defaultMessage="Configured z-Folds" />
								</TableCell>
								<TableCell>
									<FormattedMessage id="Configured Machine Groups" defaultMessage="Configured Machine Groups" />
								</TableCell>

								<TableCell />
							</TableRow>
						</TableHead>
						<TableBody>
							{isLoading === true && (
								<StyledTableRow sx={{ height: 100 }}>
									<TableCell colSpan={1000} align="center" sx={{ borderBottom: "none" }}>
										<CircularProgress color="info" data-testid="production-groups__progress__loading" />
									</TableCell>
								</StyledTableRow>
							)}
							{isError === true && isLoading === false && (
								<StyledTableRow sx={{ height: 100 }}>
									<TableCell colSpan={1000} align="center" sx={{ borderBottom: "none" }}>
										<Grid container justifyContent="center" alignItems="center" gap={2}>
											<Typography component="span" color="error" data-testid="production-groups__text__error">
												<FormattedMessage
													id="Failed to load production groups. Please try again."
													defaultMessage="Failed to load production groups. Please try again."
												/>
											</Typography>
											<IconButton onClick={onRetry} data-testid="production-groups__button__retry">
												<Refresh />
											</IconButton>
										</Grid>
									</TableCell>
								</StyledTableRow>
							)}
							{!isLoading && !isError && productionGroups.length === 0 && (
								<StyledTableRow>
									<TableCell colSpan={1000} align="center">
										<Typography color="textDisabled" data-testid="production-groups__text__no-production-groups">
											<FormattedMessage
												id="No Production Groups Configured"
												defaultMessage="No Production Groups Configured"
											/>
										</Typography>
									</TableCell>
								</StyledTableRow>
							)}
							{!isLoading &&
								!isError &&
								productionGroups.map((productionGroup) => (
									<StyledTableRow key={productionGroup.id}>
										<TableCell sx={{ fontWeight: 500 }}>{productionGroup.alias}</TableCell>
										<TableCell>{productionGroup.description}</TableCell>
										<TableCell>
											{(configuredCorrugates[productionGroup.id] ?? []).map((name, index, names) => (
												<Typography
													key={`${productionGroup.id}-${name}`}
													component="span"
													sx={[
														name === deletedZfoldLabel ? { fontStyle: "italic", color: "text.disabled" } : false,
														index < names.length - 1 ? { "&::after": { content: '", "' } } : false,
													]}
												>
													{name}
												</Typography>
											))}
										</TableCell>
										<TableCell>{configuredMachineGroups[productionGroup.id] ?? ""}</TableCell>

										<TableCell>
											<Grid container gap={1} justifyContent="flex-end">
												<ProductionGroupHistoryButton
													productionGroup={productionGroup}
													corrugates={corrugates}
													machineGroups={machineGroups}
												/>
												<ProductionGroupEditButton productionGroup={productionGroup} />
												<ProductionGroupDeleteButton productionGroup={productionGroup} />
											</Grid>
										</TableCell>
									</StyledTableRow>
								))}
						</TableBody>
					</Table>
				</TableContainer>
			</Card>
		</ConfigurationPageLayout>
	);
};
